import React, { useState, useCallback } from 'react';
import ConfirmationModal from 'sharedWebpack/ConfirmationModal';
import httpClient from 'agentHTTPClient';
import routes from 'agentRoutes';
import Button from '@welldev-group/rcl-button';
import { Spinner } from '@welldev-group/react-comp-lib';
import subscriptionImageSrc from '../../../../../apps/public/app/assets/images/public/subscription-image.png';
import SubscriptionInput from './SubscriptionInput';

const initialState = {
  firstName: '',
  lastName: '',
  email: '',
};

const defaultModalProps = {
  confirmationHeader: null,
  subHeader: null,
  type: null,
  withAction: false,
  isConfirmationOnly: false,
  confirmationText: I18n.t('public.shared.mailing_list.subscription_form.labels.close'),
};

const SubscriptionForm = () => {
  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalProps, setModalProps] = useState(defaultModalProps);

  const handleChange = useCallback((name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleBlur = useCallback(e => {
    const { name } = e.target;
    setTouched(prev => ({
      ...prev,
      [name]: true,
    }));
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setModalProps(defaultModalProps);
  }, []);

  const handleSubmit = useCallback(async () => {
    setIsLoading(true);
    setErrors({});
    setIsModalOpen(true);

    const payload = {
      mailing_list: {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
      },
    };

    try {
      await httpClient.post(routes.public.subscription.create(), payload);

      setModalProps({
        type: 'success',
        confirmationHeader: I18n.t('public.shared.mailing_list.success_msg'),
        subHeader: null,
        withAction: true,
        confirmationText: I18n.t('public.shared.mailing_list.subscription_form.labels.close'),
      });

      setFormData(initialState);
      setTouched({});
    } catch (error) {
      if (error.response?.status === 422) {
        setIsModalOpen(false);
        setErrors(error.response.data.error);
      } else {
        const errorMsg = I18n.t('public.shared.general_error_msg');

        setErrors({ generic: errorMsg });

        setModalProps({
          type: 'danger',
          confirmationHeader: errorMsg,
          withAction: true,
          confirmationText: I18n.t('public.shared.mailing_list.subscription_form.labels.close'),
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [formData]);

  const formFields = [
    {
      name: 'firstName',
      labelKey: I18n.t('public.shared.mailing_list.subscription_form.labels.first_name'),
      placeholderKey: I18n.t(
        'public.shared.mailing_list.subscription_form.placeholders.first_name',
      ),
    },
    {
      name: 'lastName',
      labelKey: I18n.t('public.shared.mailing_list.subscription_form.labels.last_name'),
      placeholderKey: I18n.t('public.shared.mailing_list.subscription_form.placeholders.last_name'),
    },
    {
      name: 'email',
      labelKey: I18n.t('public.shared.mailing_list.subscription_form.labels.email'),
      placeholderKey: I18n.t('public.shared.mailing_list.subscription_form.placeholders.email'),
    },
  ];

  return (
    <div className="subscription container d-flex-column align-center">
      <div className="subscription__title">
        <div className="subscription__title-text">
          {I18n.t('public.shared.mailing_list.subscription_form.labels.title')}
        </div>
      </div>

      <div className="subscription__title-details">
        {I18n.t('public.shared.mailing_list.subscription_form.labels.details')}
      </div>

      <div className="subscription__body">
        <div className="flex-basis-half">
          <img src={subscriptionImageSrc} className="subscription__form-image" alt="subscription" />
        </div>

        <div className="flex-basis-half subscription__form row-gap-20">
          <div className="d-flex column-gap-20 subscription__form-names">
            <SubscriptionInput
              className="flex-basis-half"
              key={formFields[0].name}
              name={formFields[0].name}
              labelKey={formFields[0].labelKey}
              placeholderKey={formFields[0].placeholderKey}
              value={formData.firstName}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched.firstName}
              error={errors.firstName}
            />

            <SubscriptionInput
              className="flex-basis-half"
              key={formFields[1].name}
              name={formFields[1].name}
              labelKey={formFields[1].labelKey}
              placeholderKey={formFields[1].placeholderKey}
              value={formData.lastName}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched.lastName}
              error={errors.lastName}
            />
          </div>

          <SubscriptionInput
            key={formFields[2].name}
            name={formFields[2].name}
            labelKey={formFields[2].labelKey}
            placeholderKey={formFields[2].placeholderKey}
            value={formData.email}
            onChange={handleChange}
            onBlur={handleBlur}
            touched={touched.email}
            error={errors.email}
          />

          <ConfirmationModal
            isModalOpen={isModalOpen}
            confirmationHeader={
              isLoading ? <Spinner bgColor="neutral" size="small" /> : modalProps.confirmationHeader
            }
            onConfirm={closeModal}
            onOutsideClick={closeModal}
            onReject={closeModal}
            isConfirmationOnly={!isLoading}
            withAction={!isLoading && modalProps.withAction}
            type={!isLoading ? modalProps.type : null}
            confirmationText={!isLoading ? modalProps.confirmationText : ''}
          />

          <Button
            className="subscription__form-button"
            version="v2"
            type="primary"
            label={I18n.t('public.shared.mailing_list.subscribe')}
            onClick={handleSubmit}
          />
        </div>
      </div>
    </div>
  );
};

export default SubscriptionForm;
